import { useEffect, useMemo, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Loader2, Upload, X, Check } from "lucide-react";

const SLOTS = [
  ["saddle", "Saddle"], ["bridle", "Bridle"], ["saddle_pad", "Saddle Pad"],
  ["harness", "Harness"], ["cart", "Cart"], ["whip", "Driving Whip"],
];
const SLOT_BY_KEY = {
  western_saddle: "saddle", english_saddle: "saddle", western_bridle: "bridle", english_bridle: "bridle",
  western_pad: "saddle_pad", english_pad: "saddle_pad", driving_harness: "harness", driving_cart: "cart", driving_whip: "whip",
};
const GROUP_LABEL = { western: "Western", english: "English", driving: "Driving" };
const QUALITY_COLOR = { basic: "bg-slate-200 text-slate-700", good: "bg-sky-200 text-sky-800", supreme: "bg-amber-200 text-amber-800" };
const DISC_GROUP = { "Barrel Racing": "western", "Cutting": "western", "Reining": "western", "Dressage": "english", "Jumpers": "english", "Eventing": "english", "Driving": "driving" };

const QBadge = ({ q, label }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${QUALITY_COLOR[q]}`}>{label || q}</span>;

const fmtRemaining = (until) => {
  const ms = new Date(until) - new Date();
  if (ms <= 0) return "Expired";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return `${d} Day${d === 1 ? "" : "s"}, ${h} Hour${h === 1 ? "" : "s"}`;
};
const fmtExpire = (until) => { try { return new Date(until).toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

// ---------------- Groom Service section (reusable: kept horse or makeover horse) ----------------
export const GroomSection = ({ horseName, statusUrl, assignUrl, sellWarning }) => {
  const [g, setG] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  const load = () => gameApi.get(statusUrl).then((r) => setG(r.data)).catch(() => setG(null));
  useEffect(() => { load(); }, [statusUrl]); // eslint-disable-line
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 60000); return () => clearInterval(t); }, []);
  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await gameApi.post(assignUrl);
      toast.success(data.renewed ? "Groom renewed — 7 more days added!" : "Groom assigned!");
      setConfirm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign groom."); } finally { setBusy(false); }
  };
  if (!g) return null;
  return (
    <div className="rounded-2xl border border-border bg-card p-3" data-testid="groom-section">
      <p className="text-xs font-bold uppercase tracking-wider text-secondary">Groom Service</p>
      {g.active ? (
        <div className="mt-2" data-testid="groom-active">
          <p className="text-sm font-bold text-emerald-600">Groom Active ✓</p>
          <p className="mt-1 text-[11px] text-muted-foreground">Automatically provides Hay, Water, Grooming, and +5 Energy each in-game day you end.</p>
          <div className="mt-2 rounded-xl bg-muted/40 px-3 py-2">
            <p className="text-[10px] font-bold uppercase text-muted-foreground">Time Remaining</p>
            <p className="text-sm font-bold text-primary" data-testid="groom-remaining">{fmtRemaining(g.groom_until)}</p>
            <p className="mt-1 text-[10px] text-muted-foreground">Expires {fmtExpire(g.groom_until)}</p>
          </div>
          <button data-testid="groom-renew" disabled={busy || g.grooms_available <= 0} onClick={() => setConfirm(true)}
            className="mt-2 h-9 w-full rounded-lg bg-secondary/15 text-xs font-bold text-secondary disabled:opacity-50 active:scale-95">
            {g.grooms_available > 0 ? `Renew Groom (+7 days) · ${g.grooms_available} available` : "Buy a Groom in Stable Upgrades to renew"}
          </button>
        </div>
      ) : (
        <div className="mt-2" data-testid="groom-inactive">
          <p className="text-sm font-semibold text-muted-foreground">No Groom Assigned</p>
          <p className="mt-1 text-[11px] text-muted-foreground">A Groom feeds, waters and grooms {horseName} automatically for 7 real-life days. Buy one in Stable Upgrades.</p>
          <button data-testid="groom-assign-open" disabled={g.grooms_available <= 0} onClick={() => setConfirm(true)}
            className="mt-2 h-9 w-full rounded-lg bg-primary text-xs font-bold text-primary-foreground disabled:opacity-50 active:scale-95">
            {g.grooms_available > 0 ? `Assign Groom (${g.grooms_available} available)` : "No Groom Service owned"}
          </button>
        </div>
      )}
      {sellWarning && (
        <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-800" data-testid="groom-sell-warning">
          ⚠️ If you auction or sell this horse in the Makeover, any remaining Groom time is lost and can't be transferred to another horse.
        </p>
      )}
      {confirm && (
        <div className="fixed inset-0 z-[96] grid place-items-center bg-black/60 p-4" data-testid="groom-confirm" onClick={() => setConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-5 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="font-display text-lg font-bold text-primary">{g.active ? "Renew Groom" : "Assign Groom"} to {horseName}?</p>
            <p className="mt-1 text-sm text-muted-foreground">{g.active ? "This adds another 7 real-life days on top of the current Groom's remaining time." : "This Groom will automatically care for this horse for 7 real-life days. Once assigned, the Groom cannot be removed or transferred to another horse."}</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="groom-cancel" onClick={() => setConfirm(false)} className="h-11 flex-1 rounded-xl border border-border font-bold text-primary active:scale-95">Cancel</button>
              <button data-testid="groom-assign-confirm" disabled={busy} onClick={assign} className="h-11 flex-1 rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">{busy ? "…" : g.active ? "Renew Groom" : "Assign Groom"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Makeover horse Tack tab (Groom Service only — no competition tack) ----------------
export const MakeoverTackTab = ({ h }) => (
  <div className="mx-4 mt-3 space-y-3" data-testid="makeover-tack-tab">
    <GroomSection horseName={h?.name || "your horse"} statusUrl="/game/makeover/groom" assignUrl="/game/makeover/groom/assign" sellWarning />
    <div className="rounded-2xl border border-border bg-card p-3">
      <p className="text-xs font-bold uppercase tracking-wider text-secondary">Performance Tack</p>
      <p className="mt-1 text-[11px] text-muted-foreground">Saddles, bridles and pads add competition bonuses for kept horses in a chosen discipline. Your makeover horse competes in the Makeover, so use this tab to add a Groom for daily-care convenience.</p>
    </div>
  </div>
);

// ---------------- Tack Shop (section inside the game shop) ----------------
export const TackShopSection = ({ keptHorses = [], onUpdate }) => {
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("all");
  const [busy, setBusy] = useState(false);
  const load = () => gameApi.get("/game/tack/catalog").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  const owned = data?.inventory || [];
  const countOf = (key, q) => owned.filter((i) => i.key === key && i.quality === q).length;
  const shown = (data?.shop || []).filter((i) => i.enabled && (group === "all" || i.group === group));
  const buy = async (it) => {
    setBusy(true);
    try { const { data: res } = await gameApi.post("/game/tack/buy", { key: it.key, quality: it.quality }); toast.success(`Bought ${it.quality_label} ${it.type}!`); load(); onUpdate && onUpdate((p) => (p ? { ...p, currency: res.currency } : p)); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not buy."); } finally { setBusy(false); }
  };
  if (!data) return <p className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading tack…</p>;
  return (
    <div className="mt-2" data-testid="tack-shop-section">
      <p className="text-[11px] text-muted-foreground">Performance tack gives a small competition bonus in matching disciplines. Buy separate Basic / Good / Supreme pieces — equip them from a horse's Tack tab.</p>
      <div className="mt-2 flex gap-1 rounded-xl bg-muted p-1">
        {[["all", "All"], ["western", "Western"], ["english", "English"], ["driving", "Driving"]].map(([k, l]) => (
          <button key={k} data-testid={`tack-group-${k}`} onClick={() => setGroup(k)}
            className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${group === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {shown.map((it) => {
          const n = countOf(it.key, it.quality);
          return (
            <div key={it.cid} data-testid={`tack-shop-item-${it.cid}`} className="rounded-2xl border border-border bg-card p-2.5">
              <div className="flex h-16 w-full items-center justify-center overflow-hidden rounded-xl bg-muted">
                {it.art_image ? <img src={it.art_image} alt={it.type} className="h-full w-full object-contain" /> : <span className="text-[10px] font-bold text-muted-foreground">{GROUP_LABEL[it.group]}</span>}
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-1"><p className="truncate text-xs font-bold text-primary">{it.type}</p><QBadge q={it.quality} label={it.quality_label} /></div>
              <p className="text-[10px] text-muted-foreground">{it.disciplines.join(", ")}</p>
              <p className="text-[10px] font-semibold text-emerald-600">+{it.bonus} competition bonus</p>
              {n > 0 && <p className="text-[10px] font-bold text-secondary">Owned ×{n}</p>}
              <button data-testid={`tack-buy-${it.cid}`} disabled={busy} onClick={() => buy(it)}
                className="mt-1.5 flex h-8 w-full items-center justify-center gap-1 rounded-lg bg-secondary text-xs font-bold text-white active:scale-95"><Coins size={12} /> {it.price}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------- Horse profile Tack tab ----------------
export const HorseTackTab = ({ a, keptHorses = [] }) => {
  const [data, setData] = useState(null);
  const [pickSlot, setPickSlot] = useState(null);
  const [moveConfirm, setMoveConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => gameApi.get("/game/tack/catalog").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  const inv = data?.inventory || [];
  const nameById = useMemo(() => Object.fromEntries(keptHorses.map((h) => [h.id, h.name])), [keptHorses]);
  const meta = useMemo(() => Object.fromEntries((data?.shop || []).map((s) => [`${s.key}_${s.quality}`, s])), [data]);
  const info = (it) => meta[`${it.key}_${it.quality}`] || {};
  const group = DISC_GROUP[a.discipline];
  const equippedBySlot = {};
  inv.forEach((it) => { if (it.equipped_by === a.id) equippedBySlot[SLOT_BY_KEY[it.key]] = it; });
  const gives = (it) => info(it).group === group;
  const doEquip = async (it) => {
    setBusy(true);
    try { await gameApi.post("/game/tack/equip", { item_id: it.id, aid: a.id }); toast.success("Equipped"); setPickSlot(null); setMoveConfirm(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not equip."); } finally { setBusy(false); }
  };
  const equip = (it) => {
    if (it.equipped_by && it.equipped_by !== a.id) { setMoveConfirm(it); return; }
    doEquip(it);
  };
  const unequip = async (it) => {
    setBusy(true);
    try { await gameApi.post("/game/tack/unequip", { item_id: it.id }); toast.success("Unequipped"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not unequip."); } finally { setBusy(false); }
  };
  if (!data) return <p className="mx-4 mt-4 text-center text-sm text-muted-foreground">Loading tack…</p>;
  const recDisc = group ? { western: "Western Saddle, Bridle & Saddle Pad", english: "English Saddle, Bridle & Saddle Pad", driving: "Driving Harness, Cart & Whip" }[group] : null;
  const optionsForSlot = (slot) => inv.filter((it) => SLOT_BY_KEY[it.key] === slot);
  return (
    <div className="mx-4 mt-3 space-y-3" data-testid="horse-tack-tab">
      <GroomSection horseName={a.name} statusUrl={`/game/alumni/${a.id}/groom`} assignUrl={`/game/alumni/${a.id}/groom/assign`} />
      {a.discipline && (
        <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-3" data-testid="tack-recommend">
          <p className="text-xs font-bold text-emerald-800">Recommended for {a.discipline}</p>
          <p className="text-[11px] text-emerald-700">{recDisc}</p>
        </div>
      )}
      <div className="rounded-2xl border border-border bg-card p-3">
        <p className="text-xs font-bold uppercase tracking-wider text-secondary">Equipped Tack</p>
        <div className="mt-2 space-y-2">
          {SLOTS.map(([slot, label]) => {
            const it = equippedBySlot[slot];
            const meta_ = it ? info(it) : null;
            const bonusActive = it && gives(it);
            return (
              <div key={slot} data-testid={`tack-slot-${slot}`} className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase text-muted-foreground">{label}</p>
                  {it ? (
                    <>
                      <div className="flex items-center gap-1.5"><p className="truncate text-sm font-bold text-primary">{meta_.type}</p><QBadge q={it.quality} label={meta_.quality_label} /></div>
                      <p className={`text-[10px] font-semibold ${bonusActive ? "text-emerald-600" : "text-muted-foreground"}`}>{bonusActive ? `+${meta_.bonus} bonus for ${a.discipline}` : "No bonus for this discipline"}</p>
                    </>
                  ) : <p className="text-sm font-semibold text-muted-foreground">None</p>}
                </div>
                {it
                  ? <button data-testid={`tack-unequip-${slot}`} disabled={busy} onClick={() => unequip(it)} className="shrink-0 rounded-lg border border-border px-2.5 py-1.5 text-xs font-bold text-primary active:scale-95">Unequip</button>
                  : <button data-testid={`tack-equip-open-${slot}`} onClick={() => setPickSlot(slot)} className="shrink-0 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-bold text-primary-foreground active:scale-95">Equip</button>}
                {it && <button data-testid={`tack-replace-${slot}`} onClick={() => setPickSlot(slot)} className="shrink-0 rounded-lg bg-secondary/15 px-2.5 py-1.5 text-xs font-bold text-secondary active:scale-95">Replace</button>}
              </div>
            );
          })}
        </div>
      </div>
      {pickSlot && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4" data-testid="tack-equip-modal" onClick={() => setPickSlot(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-display text-lg font-bold text-primary">Choose {SLOTS.find((s) => s[0] === pickSlot)[1]}</p>
              <button data-testid="tack-modal-close" onClick={() => setPickSlot(null)} className="rounded-full p-1 text-muted-foreground"><X size={18} /></button>
            </div>
            <div className="mt-2 max-h-72 space-y-1.5 overflow-y-auto pr-1">
              {optionsForSlot(pickSlot).length ? optionsForSlot(pickSlot).map((it) => {
                const m = info(it); const good = m.group === group;
                const onOther = it.equipped_by && it.equipped_by !== a.id;
                const here = it.equipped_by === a.id;
                return (
                  <button key={it.id} data-testid={`tack-option-${it.id}`} disabled={busy} onClick={() => equip(it)}
                    className="flex w-full items-center gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2 text-left active:scale-[0.99]">
                    <div className="min-w-0 flex-1"><div className="flex items-center gap-1.5"><span className="truncate text-sm font-bold text-primary">{m.type}</span><QBadge q={it.quality} label={m.quality_label} /></div>
                      <span className={`text-[10px] font-semibold ${good ? "text-emerald-600" : "text-muted-foreground"}`}>{good ? `+${m.bonus} bonus for ${a.discipline}` : "No bonus for this discipline"}</span>
                      {here && <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700">Equipped here</span>}
                      {onOther && <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700">On {nameById[it.equipped_by] || "another horse"}</span>}
                    </div>
                    <Check size={16} className="shrink-0 text-primary" />
                  </button>
                );
              }) : <p className="py-4 text-center text-xs text-muted-foreground">You don't own tack for this slot. Visit the Tack Shop.</p>}
            </div>
          </div>
        </div>
      )}
      {moveConfirm && (
        <div className="fixed inset-0 z-[97] grid place-items-center bg-black/60 p-4" data-testid="tack-move-confirm" onClick={() => setMoveConfirm(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-5 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="font-display text-lg font-bold text-primary">Move this tack?</p>
            <p className="mt-1 text-sm text-muted-foreground">This {info(moveConfirm).type} is currently equipped on <span className="font-bold">{nameById[moveConfirm.equipped_by] || "another horse"}</span>. Equipping it here will remove it from them.</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="tack-move-cancel" onClick={() => setMoveConfirm(null)} className="h-11 flex-1 rounded-xl border border-border font-bold text-primary active:scale-95">Cancel</button>
              <button data-testid="tack-move-confirm-btn" disabled={busy} onClick={() => doEquip(moveConfirm)} className="h-11 flex-1 rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">{busy ? "…" : "Move it here"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- Admin tack management ----------------
export const TackAdmin = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const load = () => gameApi.get("/game/admin/tack/config").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const setField = (cid, field, val) => setItems((its) => its.map((i) => (i.cid === cid ? { ...i, [field]: val } : i)));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { items: Object.fromEntries(items.map((i) => [i.cid, { price: i.price, bonus: i.bonus, enabled: i.enabled }])) };
      const { data } = await gameApi.post("/game/admin/tack/config", payload); setItems(data.items); toast.success("Tack settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const upload = async (cid, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(cid);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("cid", cid);
      const { data } = await gameApi.post("/game/admin/tack/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(cid, "art_image", data.art_image); toast.success("Art uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setUploading(""); }
  };
  if (!items) return <p className="mt-3 text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="mt-3 space-y-2" data-testid="admin-tack">
      <p className="text-xs text-muted-foreground">Edit price, competition bonus, availability and art for every tack item. Prices are not hardcoded.</p>
      {items.map((it) => (
        <div key={it.cid} data-testid={`admin-tack-${it.cid}`} className="rounded-xl border border-border bg-card p-2.5">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
              {it.art_image ? <img src={it.art_image} alt="" className="h-full w-full object-contain" /> : <span className="text-[8px] text-muted-foreground">no art</span>}
            </div>
            <div className="min-w-0 flex-1"><p className="truncate text-xs font-bold text-primary">{it.type}</p><QBadge q={it.quality} label={it.quality_label} /></div>
            <label className="flex cursor-pointer items-center gap-1 rounded-lg border border-border px-2 py-1 text-[10px] font-bold text-primary">
              {uploading === it.cid ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Art
              <input type="file" accept="image/png,image/webp,image/jpeg" className="hidden" data-testid={`admin-tack-upload-${it.cid}`} onChange={(e) => upload(it.cid, e)} disabled={!!uploading} />
            </label>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <label className="text-[10px] font-semibold text-muted-foreground">Price
              <input data-testid={`admin-tack-price-${it.cid}`} type="number" min="0" value={it.price} onChange={(e) => setField(it.cid, "price", e.target.value)} className="mt-0.5 w-full rounded-lg border border-border bg-background px-2 py-1 text-sm text-primary" /></label>
            <label className="text-[10px] font-semibold text-muted-foreground">Bonus
              <input data-testid={`admin-tack-bonus-${it.cid}`} type="number" step="0.1" min="0" value={it.bonus} onChange={(e) => setField(it.cid, "bonus", e.target.value)} className="mt-0.5 w-full rounded-lg border border-border bg-background px-2 py-1 text-sm text-primary" /></label>
            <label className="flex items-end gap-1 pb-1 text-[10px] font-semibold text-muted-foreground">
              <input data-testid={`admin-tack-enabled-${it.cid}`} type="checkbox" checked={it.enabled} onChange={(e) => setField(it.cid, "enabled", e.target.checked)} /> In shop</label>
          </div>
        </div>
      ))}
      <button data-testid="admin-tack-save" disabled={busy} onClick={save} className="h-11 w-full rounded-xl bg-primary font-bold text-primary-foreground">{busy ? "Saving…" : "Save Tack Settings"}</button>
    </div>
  );
};
